import ISocketHandler from './ISocketHandler';
import ISocketMessage, { SocketMessageMethods } from './ISocketMessage';

interface ISocketData {
  identifier: string;
  message: ISocketMessage;
}

export default class WebSocketConnector {
  static readonly apiHost: string = `${
    (process.env.API_HOST_URL || 'https://level.travel').split('//')[1]
  }:${process.env.SOCKET_PORT}`;

  static readonly timeOutConnection = 20 * 1000;

  static connectionPromise: Promise<WebSocket> | null = null;

  static connection: WebSocket;

  static subscriptions: {
    identifier: string;
    data?: Record<string, unknown>;
    socketHandlers: ISocketHandler[];
  }[] = [];

  static reconnectCount = 0;

  static isReconnection = false;

  private readonly CONNECTION_REQUIRED_ERROR =
    'WebSocket: Connection not open, please call createWebSocketConnection to create it';

  private readonly CLOSE_NOT_OPEN_CONNECTION_ERROR =
    'WebSocket: You are trying to close an unopened connection';

  static readonly MAX_RECONNECT_COUNT = 10;

  static readonly MAX_RECONNECT_TIME = 10 * 1000;

  constructor(socket: WebSocket) {
    if (typeof socket === 'undefined') {
      throw new Error('WebSocket: Cannot be called directly, please use build');
    }

    WebSocketConnector.connection = socket;
  }

  static async build() {
    let socket;
    if (!WebSocketConnector.connectionPromise) {
      socket = await WebSocketConnector.openWebSocketConnection();
    } else {
      socket = await WebSocketConnector.connectionPromise;
    }

    return new WebSocketConnector(socket);
  }

  static openWebSocketConnection(): Promise<WebSocket> {
    WebSocketConnector.connectionPromise = new Promise((resolve, reject) => {
      try {
        if (
          WebSocketConnector.connection &&
          WebSocketConnector.connection.readyState === WebSocket.OPEN
        ) {
          resolve(WebSocketConnector.connection);
        }

        const socket = new WebSocket(
          `wss://${WebSocketConnector.apiHost}/cable`
        );

        if (socket.readyState === WebSocket.CLOSED) {
          reject(new Error('WebSocket: Connection cannot be opened'));
        }

        const timer = setTimeout(() => {
          reject(new Error('WebSocket: Connection timed out'));
        }, WebSocketConnector.timeOutConnection);

        socket.onopen = () => {
          clearTimeout(timer);
          WebSocketConnector.isReconnection = false;

          resolve(socket);
        };

        socket.onerror = () => {
          reject(new Error('WebSocket:Connection failed'));
        };

        socket.onclose = async () => {
          try {
            if (
              !WebSocketConnector.isReconnection &&
              WebSocketConnector.reconnectCount <=
                WebSocketConnector.MAX_RECONNECT_COUNT
            ) {
              setTimeout(async () => {
                WebSocketConnector.isReconnection = true;
                WebSocketConnector.reconnectCount += 1;
                WebSocketConnector.connectionPromise = null;

                WebSocketConnector.isReconnection = false;
                const webSocketConnector = await WebSocketConnector.build();
                webSocketConnector.resubscribeSubscriptions();
              }, WebSocketConnector.MAX_RECONNECT_TIME);
            }
          } catch (e) {
            // Websocket reconnect failed
          }
        };
      } catch (e) {
        reject(e);
      }
    });

    return WebSocketConnector.connectionPromise;
  }

  public resubscribeSubscriptions() {
    WebSocketConnector.subscriptions.forEach(sub => {
      const identifier = JSON.parse(sub.identifier);
      this.subscribeChannel(identifier, true);

      if (sub.data) {
        this.updateChannel({ identifier, data: sub.data });
      }

      sub.socketHandlers.forEach(handler => {
        this.subscribeSocketOnMessage({
          identifier,
          socketHandler: handler,
        });
      });
    });
  }

  public closeWebSocketConnection(code?: number, reason?: string): void {
    if (
      WebSocketConnector.connection &&
      WebSocketConnector.connection.readyState === WebSocket.OPEN
    ) {
      WebSocketConnector.connection.close(code, reason);
    } else {
      throw Error(this.CLOSE_NOT_OPEN_CONNECTION_ERROR);
    }
  }

  public subscribeChannel(
    identifier: { channel: string; [key: string]: unknown },
    isReSubscribe = false
  ): void {
    if (
      !WebSocketConnector.connection ||
      WebSocketConnector.connection.readyState !== WebSocket.OPEN
    ) {
      throw new Error(this.CONNECTION_REQUIRED_ERROR);
    }

    const stringIdentifier = JSON.stringify(identifier);

    if (!isReSubscribe && this.hasActiveSubscription(identifier)) return;

    WebSocketConnector.connection.send(
      JSON.stringify({
        command: 'subscribe',
        identifier: stringIdentifier,
      })
    );

    if (!isReSubscribe) {
      WebSocketConnector.subscriptions.push({
        identifier: stringIdentifier,
        socketHandlers: [],
      });
    }
  }

  public updateChannel({
    data,
    identifier,
  }: {
    data: Record<string, unknown>;
    identifier: { channel: string; [key: string]: unknown };
  }): void {
    if (
      !WebSocketConnector.connection ||
      WebSocketConnector.connection.readyState !== WebSocket.OPEN
    ) {
      throw new Error(this.CONNECTION_REQUIRED_ERROR);
    }

    if (data === null) {
      throw new Error('WebSocket: Data is required');
    }

    const stringIdentifier = JSON.stringify(identifier);

    WebSocketConnector.connection.send(
      JSON.stringify({
        command: 'message',
        identifier: stringIdentifier,
        data: JSON.stringify({
          action: 'update_channel',
          ...data,
        }),
      })
    );

    WebSocketConnector.subscriptions = WebSocketConnector.subscriptions.map(
      subscription => {
        if (subscription.identifier === stringIdentifier) {
          return {
            ...subscription,
            data,
          };
        }
        return subscription;
      }
    );
  }

  public unsubscribeChannel(identifier: {
    channel: string;
    [key: string]: unknown;
  }): void {
    if (
      !WebSocketConnector.connection ||
      WebSocketConnector.connection.readyState !== WebSocket.OPEN
    ) {
      throw new Error(this.CONNECTION_REQUIRED_ERROR);
    }

    const stringIdentifier = JSON.stringify(identifier);

    if (!this.hasActiveSubscription(identifier)) return;

    WebSocketConnector.connection.send(
      JSON.stringify({
        command: 'unsubscribe',
        identifier: stringIdentifier,
      })
    );

    WebSocketConnector.subscriptions = WebSocketConnector.subscriptions.filter(
      subscription => subscription.identifier !== stringIdentifier
    );
  }

  public subscribeSocketOnMessage({
    identifier,
    socketHandler,
    isReSubscribe = false,
  }: {
    identifier: { channel: string; [key: string]: unknown };
    socketHandler: ISocketHandler;
    isReSubscribe?: boolean;
  }): (event: MessageEvent) => void {
    if (
      !WebSocketConnector.connection ||
      WebSocketConnector.connection.readyState !== WebSocket.OPEN
    ) {
      throw new Error(this.CONNECTION_REQUIRED_ERROR);
    }

    const stringIdentifier = JSON.stringify(identifier);
    WebSocketConnector.reconnectCount = 0;

    const handleMessage = (event: MessageEvent) => {
      const data: ISocketData = JSON.parse(event.data);
      if (!data.identifier || !data.message) {
        // this message does not support lt websocket protocol
        return;
      }

      const messageIdentifier = data.identifier;
      if (messageIdentifier !== stringIdentifier) return;

      const { message } = data;

      switch (message.method) {
        case SocketMessageMethods.CREATE:
          socketHandler.create(message);
          break;
        case SocketMessageMethods.DELETE:
          socketHandler.delete(message);
          break;
        case SocketMessageMethods.UPDATE:
          socketHandler.update(message);
          break;
        case SocketMessageMethods.INFO:
          socketHandler.info(message);
          break;
        default:
          throw Error('WebSocket: Message have unexpected method');
      }
    };

    if (!isReSubscribe) {
      WebSocketConnector.subscriptions = WebSocketConnector.subscriptions.map(
        subscription => {
          if (subscription.identifier === stringIdentifier) {
            return {
              ...subscription,
              socketHandlers: [...subscription.socketHandlers, socketHandler],
            };
          }
          return subscription;
        }
      );
    }

    WebSocketConnector.connection.addEventListener('message', handleMessage);
    return handleMessage;
  }

  public unsubscribeSocketOnMessage(
    handleMessage: (event: MessageEvent) => void
  ) {
    if (
      !WebSocketConnector.connection ||
      WebSocketConnector.connection.readyState !== WebSocket.OPEN
    ) {
      throw new Error(this.CONNECTION_REQUIRED_ERROR);
    }

    WebSocketConnector.connection.removeEventListener('message', handleMessage);
  }

  public hasActiveSubscription(identifier: {
    channel: string;
    [key: string]: unknown;
  }): boolean {
    const stringIdentifier = JSON.stringify(identifier);
    return !!WebSocketConnector.subscriptions.find(
      sub => sub.identifier === stringIdentifier
    );
  }
}
